// Self-contained wishlist. Stores product {id, variantId, handle} in localStorage.
// Deduped per variant, so the same product in two variants = two entries.
// Wires every [data-wish] toggle, keeps them in sync with the selected variant,
// updates header count, and renders the wishlist page ([data-wishlist-page])
// from /products/{handle}?view=wishlist-card.
(() => {
  const KEY = 'sj.wishlist';
  const EVT = 'sj:wishlist';
  const rootUrl = window.Shopify?.routes?.root || '/';

  const read = () => {
    try { return JSON.parse(localStorage.getItem(KEY)) || []; }
    catch { return []; }
  };
  const write = (list) => {
    localStorage.setItem(KEY, JSON.stringify(list));
    document.dispatchEvent(new CustomEvent(EVT, { detail: { list } }));
  };
  const has = (variantId, list = read()) =>
    list.some((p) => String(p.variantId) === String(variantId));
  const toggle = (id, variantId, handle) => {
    const list = read();
    const i = list.findIndex((p) => String(p.variantId) === String(variantId));
    if (i > -1) list.splice(i, 1);
    else list.push({ id: String(id), variantId: String(variantId), handle });
    write(list);
    return i === -1; // true = now saved
  };

  // The variant a wish button currently refers to: the live selection in the
  // nearest add-to-cart form (card or PDP), else the button's default variant.
  const currentVariantId = (btn) => {
    const form =
      btn.closest('[data-add-to-cart]') ||
      btn.closest('.pc-card')?.querySelector('[data-add-to-cart]');
    return (
      form?.querySelector('input[name="id"]')?.value ||
      btn.getAttribute('data-wish-variant') ||
      ''
    );
  };

  // --- Toggle buttons (PDP + cards) ---------------------------------------
  // aria-pressed stuurt ook de opvulling van het hartje aan, via
  // .pc-wish/.pdp-wish[aria-pressed="true"] svg{fill:currentColor} in base.css.
  const paint = (btn, on) => {
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    btn.setAttribute('aria-label', on ? 'Verwijder uit verlanglijst' : 'Voeg toe aan verlanglijst');
  };

  const syncButtons = (root) => {
    const list = read();
    (root || document).querySelectorAll('[data-wish]').forEach((btn) => {
      paint(btn, has(currentVariantId(btn), list));
    });
  };

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wish]');
    if (!btn) return;
    e.preventDefault();
    const id = btn.getAttribute('data-wish');
    const handle = btn.getAttribute('data-wish-handle') || '';
    const variantId = currentVariantId(btn);
    const now = toggle(id, variantId, handle);
    paint(btn, now);
  });

  // Rails en mega-panels worden via de Section Rendering API ingeladen; de
  // kaarten daarin komen ongesynct binnen.
  document.addEventListener('sj:content-replaced', (e) => syncButtons(e.detail?.container));

  // Re-sync aria state when the shopper switches variant (card swatches / PDP
  // options). Deferred so the variant controllers set input[name="id"] first.
  const resync = () => setTimeout(syncButtons, 0);
  document.addEventListener('click', (e) => {
    if (e.target.closest('.pc-opt')) resync();
  });
  document.addEventListener('change', (e) => {
    if (e.target.matches('input[name^="option"]')) resync();
  });

  // --- Header count -------------------------------------------------------
  const syncCount = () => {
    const n = read().length;
    document.querySelectorAll('[data-wish-count]').forEach((el) => {
      el.textContent = n;
      el.hidden = n === 0;
    });
    // The visible badge is aria-hidden (see header.liquid), so the count is announced
    // through this sr-only live region instead.
    document.querySelectorAll('[data-wish-count-label]').forEach((el) => {
      el.textContent = `Verlanglijst: ${n} artikelen`;
    });
  };

  // --- Wishlist page ------------------------------------------------------
  // One request per entry: templates/product.wishlist-card.liquid renders the
  // finished card and applies the eligibility rules server-side. It replaces a
  // pair of fetches per item, a full product HTML document read only for its
  // `product-stock-eligible` meta, plus /products/<handle>.js for the data.
  //
  // Deliberately not cached: the card carries price, compare_at_price and the
  // stock line, and the Easy:Sale app rewrites compare_at_price on the
  // storefront. A wishlist page is also exactly where a shopper expects to see
  // the current price.
  const fetchCard = async (item) => {
    if (!item.handle) return null;
    const url = `${rootUrl}products/${encodeURIComponent(item.handle)}?view=wishlist-card`
      + (item.variantId ? `&variant=${encodeURIComponent(item.variantId)}` : '');
    try {
      const res = await fetch(url, { headers: { 'X-Requested-With': 'fetch' } });
      if (!res.ok) return null;
      // Empty body = the product is no longer eligible (uitverkocht, hidden,
      // untracked). Drop it from the grid, same as the old meta-tag check did.
      const html = (await res.text()).trim();
      if (!html) return null;
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const card = tmp.firstElementChild;
      if (!card) return null;
      // The stored variant may have been deleted since it was wished; the
      // template then falls back to another variant. Stamp the stored id back so
      // the remove button still targets the right localStorage row.
      card.querySelector('[data-wish]')?.setAttribute('data-wish-variant', item.variantId ?? '');
      return card;
    } catch { return null; }
  };

  const renderPage = async (root) => {
    const grid = root.querySelector('[data-wishlist-grid]');
    const empty = root.querySelector('[data-wishlist-empty]');
    const list = read();
    if (!list.length) {
      grid.replaceChildren();
      if (empty) empty.hidden = false;
      return;
    }
    if (empty) empty.hidden = true;
    const cards = (await Promise.all(list.map(fetchCard))).filter(Boolean);
    if (!cards.length) {
      grid.replaceChildren();
      if (empty) empty.hidden = false;
      return;
    }
    grid.replaceChildren(...cards);
  };

  // --- Boot ---------------------------------------------------------------
  const boot = () => {
    // Restant van de tweede wishlist die in global.js zat; nooit gelezen.
    try { localStorage.removeItem('sj.wish'); } catch {}
    syncButtons();
    syncCount();
    const page = document.querySelector('[data-wishlist-page]');
    if (page) renderPage(page);
  };

  document.addEventListener(EVT, () => {
    syncButtons();
    syncCount();
    const page = document.querySelector('[data-wishlist-page]');
    if (page) renderPage(page);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
